import type { ConnectorAccessProbeResult, ConnectorDefinition } from "../types";

const PROBE_TIMEOUT_MS = 8000;

/**
 * GitHub MCP tools that only READ. Always available once the connector is
 * live. Curated allowlist across the toolsets we support: repos, issues, pull
 * requests, actions, projects, discussions, org/user context, and security
 * alerts. Names are the bare MCP tool names the GitHub remote server
 * advertises (see https://github.com/github/github-mcp-server).
 */
export const GITHUB_READ_TOOLS = [
  // Context / orgs / users
  "get_me",
  "get_teams",
  "get_team_members",
  "search_orgs",
  "search_users",
  // Repos
  "search_repositories",
  "get_file_contents",
  "get_repository_tree",
  "list_branches",
  "list_commits",
  "get_commit",
  "list_tags",
  "get_tag",
  "list_releases",
  "get_latest_release",
  "get_release_by_tag",
  "list_repository_collaborators",
  "search_code",
  "search_commits",
  // Issues
  "issue_read",
  "list_issues",
  "search_issues",
  "list_issue_types",
  "list_issue_fields",
  "get_label",
  "list_label",
  // Pull requests
  "pull_request_read",
  "list_pull_requests",
  "search_pull_requests",
  // Actions
  "actions_list",
  "actions_get",
  "get_job_logs",
  // Projects
  "projects_list",
  "projects_get",
  // Discussions
  "list_discussions",
  "get_discussion",
  "get_discussion_comments",
  "list_discussion_categories",
  // Security alerts (repo scope accepted)
  "list_code_scanning_alerts",
  "get_code_scanning_alert",
  "list_dependabot_alerts",
  "get_dependabot_alert",
] as const;

/**
 * GitHub MCP tools that WRITE / mutate. Deliberately minimal: the ONLY write
 * tool exposed is `issue_write` (create or update an issue). Every other write
 * the GitHub MCP server offers — `add_issue_comment`, `sub_issue_write`,
 * `create_pull_request`, `push_files`, `create_or_update_file`, `projects_write`,
 * `label_write`, gist/notification/star tools, etc. — is intentionally NOT in
 * the allowlist so it never reaches the model.
 *
 * There is no separate write feature flag for GitHub, so this list is NOT
 * passed to the capability as `writeToolNames` (that would filter it against
 * `ctx.writableConnectors`, which never contains `"github"`). It exists here to
 * document the read/write split for tests and the UI tool registry. Safety for
 * `issue_write` comes entirely from the mandatory HITL approval gate declared
 * on the capability.
 */
export const GITHUB_WRITE_TOOLS = ["issue_write"] as const;

async function validateGithubAccess(
  token: string
): Promise<ConnectorAccessProbeResult> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);

  try {
    // `GET /user` is the cheapest authenticated endpoint — a 200 confirms the
    // OAuth token is valid and identifies the connected user. Org/SSO access
    // is per-org and handled by GitHub during the authorize flow, so we don't
    // probe it here.
    const response = await fetch("https://api.github.com/user", {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/vnd.github+json",
        "X-GitHub-Api-Version": "2022-11-28",
      },
      signal: controller.signal,
    });

    if (response.ok) {
      return { ok: true };
    }

    const body = await response.text().catch(() => "");
    if (response.status === 401 || response.status === 403) {
      return {
        ok: false,
        message:
          "GitHub rejected the connection. Reconnect, and if your organization uses SAML SSO make sure you authorize this app for that organization.",
      };
    }
    const snippet = body.slice(0, 200).replace(/\s+/g, " ").trim();
    return {
      ok: false,
      message: `GitHub rejected the connection (${response.status})${snippet ? `: ${snippet}` : "."}`,
    };
  } catch (err) {
    if (err instanceof Error && err.name === "AbortError") {
      return {
        ok: false,
        message: `GitHub did not respond within ${PROBE_TIMEOUT_MS / 1000}s. Try connecting again.`,
      };
    }
    const detail = err instanceof Error ? err.message : String(err);
    return {
      ok: false,
      message: `Could not reach GitHub: ${detail}`,
    };
  } finally {
    clearTimeout(timeout);
  }
}

/**
 * GitHub connector — OAuth App-backed MCP. Authentication uses GitHub's
 * standard OAuth 2.0 authorization-code flow (github.com is the identity
 * provider); the resulting per-user access token is injected as
 * `Authorization: Bearer` into GitHub's hosted remote MCP server at
 * `https://api.githubcopilot.com/mcp/`.
 *
 * One GitHub identity spans every org the user belongs to. Orgs with
 * third-party app restrictions require an admin to approve the OAuth App, and
 * orgs wired with SAML/Entra SSO prompt the user to authorize the app for that
 * org during connect (or later at github.com/settings/applications). None of
 * that is enforced in code — it's handled by GitHub during the authorize flow.
 *
 * OAuth App tokens do not expire, so there is no refresh flow.
 */
export const githubConnector: ConnectorDefinition = {
  id: "github",
  name: "GitHub",
  description:
    "Read your GitHub repos, PRs, issues, actions, projects, and org data, and create or update issues (with your approval)",
  icon: "github",
  authType: "oauth",
  kind: "mcp",
  oauth: {
    authorizationUrl: "https://github.com/login/oauth/authorize",
    tokenUrl: "https://github.com/login/oauth/access_token",
    scopes: ["repo", "read:org", "read:project"],
    clientIdEnvVar: "GITHUB_CLIENT_ID",
    clientSecretEnvVar: "GITHUB_CLIENT_SECRET",
  },
  mcp: {
    serverUrl: "https://api.githubcopilot.com/mcp/",
    transport: "http",
    toolAllowlist: [...GITHUB_READ_TOOLS, ...GITHUB_WRITE_TOOLS],
    // Documents the read/write split for tests and the UI registry. It does
    // NOT gate anything by itself — gating only happens when the capability
    // passes `writeToolNames`, which the GitHub capability deliberately does
    // not (single flag; `issue_write` is guarded by mandatory HITL instead).
    writeToolNames: [...GITHUB_WRITE_TOOLS],
  },
  isEnabled: () =>
    Boolean(process.env.GITHUB_CLIENT_ID && process.env.GITHUB_CLIENT_SECRET),
  validateAccess: validateGithubAccess,
};
